import { isEmpty, max, min } from "lodash";

import { Product } from "~/bff/types/Product";
import { Variant } from "~/bff/types/Variant";
import { Variants } from "~/bff/types/Variants";
import { WidgetVariants } from "~/bff/types/WidgetVariants";
import { CURRENCY_CODE, DEFAULT_LOCALE, LOCALES } from "~/constants/i18n";
import { Nullable } from "~/types/general.types";
import { formatCurrency } from "~/utils/currency/format-currency";

export const DEFAULT_FROM_LABEL = "from";

export const getDisplayPrice = (
  locale: LOCALES,
  currencyCode: string,
  price: number | null | undefined,
  isRawPrice: boolean,
): string =>
  formatCurrency(locale, currencyCode, isRawPrice ? (price ?? 0) / 100 : price ?? 0);

export const formatPriceToNumber = (
  value: string | number | null | undefined,
): number => {
  if (value === null || value === undefined) {
    return 0;
  }
  return typeof value === "string"
    ? Number(`${value}`?.replace(/[^0-9-]+/g, ""))
    : value;
};

export interface GetDisplayPriceFromVariantsParams {
  variants:
    | Nullable<Variant>[]
    | Nullable<Variants>[]
    | Nullable<WidgetVariants>[]
    | null
    | undefined;
  masterVariant?: { price?: string | null; isActive?: boolean | null };
  locale?: LOCALES;
  currencyCode?: string;
  fromLabel?: Nullable<string>;
  skipZeroPrice?: boolean;
}

// handles 2 shapes of variants data we can receive from bff
// 1 - all variants (including masterVariant) might come in "variants" field
// 2 - masterVariant might come separately from rest of variants
export const getDisplayPriceFromVariants = ({
  variants,
  masterVariant,
  locale = DEFAULT_LOCALE,
  currencyCode = CURRENCY_CODE,
  fromLabel = DEFAULT_FROM_LABEL,
  skipZeroPrice,
}: GetDisplayPriceFromVariantsParams): string | undefined | null => {
  if (isEmpty(variants) && masterVariant?.isActive) {
    return masterVariant?.price;
  }

  let prices = variants?.map((variant) => {
    let productPrice;
    if (variant && "sku_price" in variant) {
      productPrice = variant.sku_price;
    } else if (variant && "price" in variant) {
      productPrice = variant.price;
    }
    return formatPriceToNumber(productPrice);
  });

  if (prices && masterVariant?.isActive) {
    prices.push(formatPriceToNumber(masterVariant?.price));
  }

  if (skipZeroPrice) {
    prices = prices?.filter((price) => price > 0);
  }

  const priceRange = [min(prices), max(prices)];
  const price = getDisplayPrice(locale, currencyCode, priceRange[0] as number, true);
  const isEqualPrice = priceRange[0] === priceRange[1];
  return isEqualPrice ? price : `${fromLabel} ${price}`;
};

export interface GetDisplayPriceWithClearanceFromVariantsParams {
  variants: Nullable<Variant>[] | null | undefined;
  masterVariant?: Product;
  skipZeroPrice?: boolean;
}

export const getDisplayPriceWithClearanceFromVariants = ({
  variants,
  masterVariant,
  skipZeroPrice,
}: GetDisplayPriceWithClearanceFromVariantsParams): {
  isRange: boolean;
  regular: number | undefined | null;
  clearance: number | undefined | null;
} => {
  if (isEmpty(variants) && masterVariant?.isProductActive) {
    return {
      isRange: false,
      regular: masterVariant?.rawPrice,
      clearance: masterVariant?.rawClearancePrice,
    };
  }

  let prices = variants?.map((variant) => ({
    regular: variant?.rawPrice,
    clearance: variant?.rawClearancePrice,
  }));

  if (prices && masterVariant?.isProductActive) {
    prices.push({
      regular: masterVariant?.rawPrice,
      clearance: masterVariant?.rawClearancePrice,
    });
  }

  if (skipZeroPrice) {
    prices = prices?.filter(
      (price) => Boolean(price?.clearance) || Boolean(price?.regular),
    );
  }

  prices = prices?.sort((a, b) => {
    const getPriorityValue = (price: {
      regular: number | undefined | null;
      clearance: number | undefined | null;
    }) => {
      const clearance = (price.clearance ?? 0) > 0 ? price.clearance : null;
      const regular = (price.regular ?? 0) > 0 ? price.regular : null;

      if (clearance && regular) {
        return Math.min(clearance, regular);
      }

      return clearance || regular || Infinity;
    };

    return getPriorityValue(a) - getPriorityValue(b);
  });

  const isRange =
    (prices?.[0]?.clearance || prices?.[0]?.regular) !==
    (prices?.[prices.length - 1]?.clearance || prices?.[prices.length - 1]?.regular);

  return {
    isRange,
    regular: prices?.[0]?.regular,
    clearance: prices?.[0]?.clearance,
  };
};

export const getDisplayPriceWithoutCurrency = (
  price?: string | null,
): string | undefined => {
  return price
    ? (Number(price.replace(/[^0-9-]+/g, "")) / 100).toFixed(2)
    : undefined;
};

export const getPriceWithFixedPoint = (price?: number | null): number => {
  return Number((Number(price || null) / 100).toFixed(2));
};
